import type { ApiPaginatedResponse, ApiResponse } from '@bcube/shared';
import { api } from '@/lib/api';

// Types
export interface Device {
  id: string;
  model: string;
  created_at: string;
  equipment: {
    serial: string;
    lora_keys?: {
      device_eui?: string;
      device_address?: string;
      application_eui?: string;
      application_key?: string;
      application_session_key?: string;
      network_session_key?: string;
      timestamp?: string;
      kora_id?: string;
      kora_active?: boolean;
      kora_imported?: boolean;
      kora_job_scheduled_id?: string;
    };
  };
}

export const JobStatus = {
  JS_PENDING: 'JS_PENDING',
  JS_PROCESSING: 'JS_PROCESSING',
  JS_SUCCESS: 'JS_SUCCESS',
  JS_FAILED: 'JS_FAILED',
} as const;

export type JobStatus = (typeof JobStatus)[keyof typeof JobStatus];

export interface Job {
  id: string;
  status: JobStatus;
  created_at: string;
  completed_at?: string;
  payload: {
    serial: string;
    job_category: string;
    author?: {
      user_uuid?: string;
      name?: string;
    };
  };
}

export interface BatchJob {
  id: string;
  batch_metadata: {
    uuid: string;
    type: string;
    file_name?: string;
  };
  job_category: string;
  author: {
    user_uuid: string;
    name: string;
  };
  created_at: number;
}

export interface PaginatedResponse<T> {
  docs: T[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

export interface DeviceQueryParams {
  page?: number;
  limit?: number;
  serial?: string;
  model?: string;
  activated?: boolean;
}

export interface JobQueryParams {
  page?: number;
  limit?: number;
  serial?: string;
  status?: JobStatus;
}

export interface Technology {
  id: string;
  name: {
    system: string;
    brand: string;
  };
}

// Activation types
export interface ActivateDevicePayload {
  type: 'activate' | 'deactivate';
  device: {
    bcube_device_id: string;
    kora_device_id?: string;
    serial: string;
  };
  author: {
    user_uuid: string;
    name: string;
  };
}

export interface BatchActivatePayload {
  type: 'activate' | 'deactivate';
  batch_metadata: Array<{
    serials: string[];
    type: string;
    file_name?: string;
  }>;
  author: {
    user_uuid: string;
    name: string;
  };
}

export interface BatchActivateResponse {
  results: {
    fulfilled: Array<{ serial: string; status: boolean }>;
    rejected: Array<{ serial: string; reason: string; status: boolean }>;
  };
}

export interface SyncKoraPayload {
  author: {
    user_uuid: string;
    name: string;
  };
}

// Create device in KORA payload
export interface CreateDevicePayload {
  device_eui: string;
  serial: string;
  device_address: string;
  app_eui: string;
  app_key: string;
  app_session_key: string;
  network_key: string;
  author: {
    user_uuid: string;
    name: string;
  };
}

export interface CreateDeviceResponse {
  job_uuid: string;
}

// API client
export const loraApi = {
  // Sync KORA devices - Creates job and triggers async sync via Pulsar
  syncKora: async (payload: SyncKoraPayload): Promise<void> => {
    await api.post('/connectivity/lora-keys/sync', payload);
  },

  // Create device in KORA - Creates job and triggers async device creation via Pulsar
  createDevice: async (payload: CreateDevicePayload): Promise<CreateDeviceResponse> => {
    const { data } = await api.post<ApiResponse<CreateDeviceResponse>>(
      '/connectivity/lora-keys/create',
      payload,
    );
    return data.data;
  },

  // Single device activation - Creates job and triggers async activation via Pulsar
  activate: async (payload: ActivateDevicePayload): Promise<void> => {
    await api.post('/connectivity/lora-keys/activate', payload);
  },

  // Batch activation - Creates multiple jobs and triggers async activation via Pulsar
  batchActivate: async (payload: BatchActivatePayload): Promise<BatchActivateResponse> => {
    const { data } = await api.post<ApiResponse<BatchActivateResponse>>(
      '/connectivity/lora-keys/batch-activate',
      payload,
    );
    return data.data;
  },

  // Find devices from production module
  findDevices: async (params: DeviceQueryParams): Promise<PaginatedResponse<Device>> => {
    const { data } = await api.get<ApiPaginatedResponse<Device>>('/production/devices', { params });
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  // Find activated devices (kora_active = true)
  findActivatedDevices: async (
    params: Omit<DeviceQueryParams, 'activated'>,
  ): Promise<PaginatedResponse<Device>> => {
    const { data } = await api.get<ApiPaginatedResponse<Device>>('/production/devices', {
      params: {
        ...params,
        kora_active: true,
      },
    });
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  // Find deactivated devices (kora_active = false or not set, but has lora_keys)
  findDeactivatedDevices: async (
    params: Omit<DeviceQueryParams, 'activated'>,
  ): Promise<PaginatedResponse<Device>> => {
    const { data } = await api.get<ApiPaginatedResponse<Device>>('/production/devices', {
      params: {
        ...params,
        kora_active: false,
      },
    });
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  // Find KORA synced devices (devices that have kora_id)
  findKoraDevices: async (params: {
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Device>> => {
    const { data } = await api.get<ApiPaginatedResponse<Device>>('/production/devices', {
      params: {
        ...params,
        kora_imported: true,
      },
    });
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  // Find jobs (single activation logs)
  findJobs: async (params: JobQueryParams): Promise<PaginatedResponse<Job>> => {
    const { data } = await api.get<ApiPaginatedResponse<Job>>('/connectivity/jobs', { params });
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  // Find batch jobs (grouped by batch_metadata.uuid)
  findBatchJobs: async (params: JobQueryParams): Promise<PaginatedResponse<BatchJob>> => {
    const { data } = await api.get<ApiPaginatedResponse<BatchJob>>('/connectivity/jobs/batches', {
      params,
    });
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  // Find jobs by batch UUID
  findJobsByBatchUuid: async (batchUuid: string): Promise<PaginatedResponse<Job>> => {
    const { data } = await api.get<ApiPaginatedResponse<Job>>(
      `/connectivity/jobs/batch/${batchUuid}`,
    );
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  // Find technologies (for model select) - uses production devices models
  findTechnologies: async (): Promise<Technology[]> => {
    // Return static list of known LoRa device models. BWS LL NB and BWS BAIT NB (without
    // "+LoRa") have no LoRa radio, so they never have LoRa keys to activate.
    return [
      { id: '1', name: { system: 'DM_BWS_LORA', brand: 'DM BWS LoRa' } },
      { id: '2', name: { system: 'DM_BWS_NB2_LORA', brand: 'DM BWS NB2 LoRa' } },
      { id: '4', name: { system: 'DM_BWS_LL_NB_LORA', brand: 'DM BWS LL NB+LoRa' } },
      { id: '6', name: { system: 'DM_BWS_BAIT_NB_LORA', brand: 'DM BWS BAIT NB+LoRa' } },
    ];
  },
};
